/**
 * @file index.jsx
 * @brief Renders the catalog administration workspace for locations and categories.
 */
import { useEffect, useMemo, useState } from "react";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import CategoriesPanel from "./components/CategoriesPanel";
import LocationsPanel from "./components/LocationsPanel";
import styles from "./catalog-admin.module.css";


/**
 * Display the catalog administration tabs available to admin users only.
 * @returns {JSX.Element|null} The catalog admin workspace, or null for unauthorized users.
 */
export default function CatalogAdmin() {
    const { t } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();
    const { profile } = useUserProfile();
    const accessToken = auth.user?.access_token;
    const api = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );

    const [activeTab, setActiveTab] = useState("locations");

    const groupNames = profile?.groups || [];
    const isAdmin = groupNames.includes("admins");

    useEffect(() => {
        if (profile && !isAdmin) {
            navigate("/", { replace: true });
        }
    }, [isAdmin, navigate, profile]);

    if (!isAdmin) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.tabsBar} role="tablist" aria-label={t("catalogAdmin.tabAriaLabel")}>
                <button
                    className={`${styles.tabButton} ${activeTab === "locations" ? styles.tabButtonActive : ""}`}
                    onClick={() => setActiveTab("locations")}
                    role="tab"
                    aria-selected={activeTab === "locations"}
                    type="button"
                >
                    <span>{t("catalogAdmin.locationsTab")}</span>
                    <small>{t("catalogAdmin.locationsTabHint")}</small>
                </button>
                <button
                    className={`${styles.tabButton} ${activeTab === "categories" ? styles.tabButtonActive : ""}`}
                    onClick={() => setActiveTab("categories")}
                    role="tab"
                    aria-selected={activeTab === "categories"}
                    type="button"
                >
                    <span>{t("catalogAdmin.categoriesTab")}</span>
                    <small>{t("catalogAdmin.categoriesTabHint")}</small>
                </button>
            </div>

            <div className={styles.tabPanel}>
                {activeTab === "locations" ? (
                    <LocationsPanel api={api} t={t} />
                ) : (
                    <CategoriesPanel api={api} t={t} />
                )}
            </div>
        </div>
    );
}